import type { MockSectionCustomerInput } from './mockSectionData.util';

/** Snapshot source for third-party benchmark content (not customer warehouse data). */
export const WHATS_POSSIBLE_AT_MATURITY_BENCHMARK_SOURCE = 'benchmark';

/** One metric tile in the OBH Wisconsin maturity benchmark panel. */
export interface WhatsPossibleAtMaturityMetric {
  type: 'roi' | 'delta' | 'currency';
  label: string;
  value: string;
  subtitle?: string;
  accent?: boolean;
}

/** Static Studio copy for the maturity benchmark section. */
export const WHATS_POSSIBLE_AT_MATURITY_OVERLINE = "WHAT'S POSSIBLE AT MATURITY";

/** Intro paragraph below the maturity benchmark headline. */
export const WHATS_POSSIBLE_AT_MATURITY_SUMMARY =
  "In our most mature value-based contract, Reema's model has produced the following actuarially validated results.";

/**
 * Headline parts for rich rendering (customer name kept on one line).
 * @param customerName - Customer display name (e.g. "UHC Florida").
 * @returns Prefix, customer name, and trailing punctuation.
 */
export const buildWhatsPossibleAtMaturityHeadlineParts = (
  customerName: string,
): { prefix: string; customerName: string; suffix: string } => ({
  prefix: 'Validated results from',
  customerName,
  suffix: '.',
});

/**
 * Headline personalized to the report customer.
 * @param customerName - Customer display name (e.g. "UHC Florida").
 * @returns Section headline.
 */
export const buildWhatsPossibleAtMaturityHeadline = (
  customerName: string,
): string => {
  const { prefix, customerName: name, suffix } =
    buildWhatsPossibleAtMaturityHeadlineParts(customerName);
  return `${prefix} ${name}${suffix}`;
};

/**
 * Source citation personalized to the report customer.
 * @param customerName - Customer display name.
 * @returns Italic source line below the metrics panel.
 */
export const buildWhatsPossibleAtMaturitySource = (
  customerName: string,
): string =>
  `Source: Reema's actuarial analysis of the ${customerName} contract.`;

/**
 * Full section copy with customer-specific headline and source.
 * @param customerName - Customer display name.
 * @returns Overline, headline, summary, and source strings.
 */
export const buildWhatsPossibleAtMaturityCopy = (customerName: string) => ({
  overline: WHATS_POSSIBLE_AT_MATURITY_OVERLINE,
  headline: buildWhatsPossibleAtMaturityHeadline(customerName),
  summary: WHATS_POSSIBLE_AT_MATURITY_SUMMARY,
  source: buildWhatsPossibleAtMaturitySource(customerName),
});

/** Fixed OBH Wisconsin actuarially validated benchmark metrics (ROI subtitle is customer-specific). */
export const WHATS_POSSIBLE_AT_MATURITY_BENCHMARK_METRICS: WhatsPossibleAtMaturityMetric[] =
  [
    {
      type: 'roi',
      label: 'ROI',
      value: '3.2:1 ROI',
    },
    {
      type: 'delta',
      label: 'BH Inpatient Admits',
      value: '↓\u00a033%',
    },
    {
      type: 'delta',
      label: 'ED Visits',
      value: '↓\u00a012%',
    },
    {
      type: 'currency',
      label: 'PMPM Savings (25%)',
      value: '$395',
      accent: true,
    },
  ];

/**
 * ROI subtitle line personalized to the report customer.
 * @param customerName - Customer display name (e.g. "UHC Florida").
 * @returns Subtitle shown under the ROI value.
 */
export const buildWhatsPossibleAtMaturityRoiSubtitle = (
  customerName: string,
): string => `achieved in ${customerName}`;

/**
 * Benchmark metrics with the ROI subtitle set for the given customer.
 * @param customerName - Customer display name.
 * @returns Metrics array for snapshot and mock data.
 */
export const buildWhatsPossibleAtMaturityMetrics = (
  customerName: string,
): WhatsPossibleAtMaturityMetric[] =>
  WHATS_POSSIBLE_AT_MATURITY_BENCHMARK_METRICS.map(metric =>
    metric.type === 'roi'
      ? {
          ...metric,
          subtitle: buildWhatsPossibleAtMaturityRoiSubtitle(customerName),
        }
      : metric,
  );

/**
 * Builds the whats-possible-at-maturity snapshot from the OBH Wisconsin benchmark.
 * @param customer - Report customer context for ROI subtitle personalization.
 * @param reportingPeriod - Human-readable report period label.
 * @returns Snapshot with static benchmark metrics and Studio copy.
 */
export const buildWhatsPossibleAtMaturitySnapshot = (
  customer: MockSectionCustomerInput,
  _reportingPeriod: string,
): Record<string, unknown> => ({
  ...buildWhatsPossibleAtMaturityCopy(customer.name),
  metrics: buildWhatsPossibleAtMaturityMetrics(customer.name),
  citationLabel: 'OBH Wisconsin — full report',
  citationUrl: 'https://www.obhwisconsin.org/outcomes',
  note:
    'External, third-party-validated results from a mature Reema deployment — what your program can grow into.',
  snapshotSource: WHATS_POSSIBLE_AT_MATURITY_BENCHMARK_SOURCE,
  fieldSources: {
    metrics: 'benchmark',
    headline: 'benchmark',
    summary: 'benchmark',
    source: 'benchmark',
  },
});
